<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import LocalizeWord from "@/components/Shared/LocalizeWord.vue";
import InlineSvg from "vue-inline-svg";

interface Props {
  items: any[];
  isTitleVisible?: boolean;
}

const props = withDefaults(defineProps<Props>(), { isTitleVisible: true });

const emit = defineEmits<{
  (e: "itemClick", item: any): void;
}>();

const { t, te } = useI18n();

const sortedItems = computed(() => {
  const sortedItems = [...props.items];
  return sortedItems.sort((a, b) => a.sort - b.sort);
});

const cleanAndTranslate = (str: string) => {
  const key = str.replace(/({|})/g, "");
  return te(key) ? t(key) : str;
};

const itemClick = (item: any) => {
  emit("itemClick", item);
};
</script>

<template>
  <nav class="flex flex-col font-semibold">
    <template v-for="(item, index) in sortedItems">
      <router-link
        v-if="item.uri.action == 'app'"
        :key="`item-${index}`"
        v-hover-prefetch-url="{ url: item.uri.uri }"
        :to="item.uri.uri[0] === '/' ? item.uri.uri : '/' + item.uri.uri"
        class="navitem flex items-center w-10 px-1 py-2 my-1 text-core-text"
        :style="{ color: item.textColor }"
        :title="cleanAndTranslate(item.title)"
        @click="itemClick(item)"
      >
        <div v-if="item.icon" class="w-8 h-8 py-1 px-1 icon flex items-center flex-shrink-0" :class="item.icon">
          <img v-if="!item.icon.endsWith('.svg')" :src="item.icon" aria-hidden="true" />
          <inline-svg
            v-if="item.icon.endsWith('.svg')"
            :src="item.icon"
            class="fill-current max-w-6 max-h-6 w-6"
            :style="{ fill: item.textColor }"
            aria-hidden="true"
          />
        </div>

        <div v-if="!item.icon" class="w-6 icon icon-default">
          <LocalizeWord :value="item.title"></LocalizeWord>
        </div>

        <div v-if="isTitleVisible" class="icon-title whitespace-nowrap">
          <LocalizeWord :value="item.title"></LocalizeWord>
        </div>
      </router-link>

      <router-link
        v-if="item.uri.action == 'tiles'"
        :key="`item-${index}`"
        :to="'/tiles/' + item.uri.uri"
        class="navitem flex items-center w-10 px-1 py-2 my-1 text-core-text focus:shadow-outline"
        :style="{ color: item.textColor }"
        :title="cleanAndTranslate(item.title)"
        @click="itemClick(item)"
      >
        <div v-if="item.icon" class="w-8 h-8 py-1 px-1 icon flex items-center flex-shrink-0" :class="item.icon">
          <img v-if="!item.icon.endsWith('.svg')" :src="item.icon" aria-hidden="true" />
          <inline-svg
            v-if="item.icon.endsWith('.svg')"
            :src="item.icon"
            class="fill-current max-w-6 max-h-6 w-6"
            :style="{ fill: item.textColor }"
            alt=""
            aria-hidden="true"
          />
        </div>
        <div v-if="!item.icon" class="w-6 icon icon-default">
          <LocalizeWord :value="item.title"></LocalizeWord>
        </div>
        <div v-if="isTitleVisible" class="icon-title whitespace-nowrap">
          <LocalizeWord :value="item.title"></LocalizeWord>
        </div>
      </router-link>

      <a
        v-if="item.uri.action == 'blank'"
        :key="`item-${index}`"
        :href="item.uri.uri"
        target="_blank"
        class="navitem flex items-center w-10 px-1 py-2 my-1 text-core-text"
        :style="{ color: item.textColor }"
        :title="cleanAndTranslate(item.title)"
        @click="itemClick(item)"
      >
        <div v-if="item.icon" class="w-8 h-8 py-1 px-1 icon flex items-center flex-shrink-0" :class="item.icon">
          <img v-if="!item.icon.endsWith('.svg')" :src="item.icon" aria-hidden="true" />
          <inline-svg
            v-if="item.icon.endsWith('.svg')"
            :src="item.icon"
            class="fill-current max-w-6 max-h-6 w-6"
            :style="{ fill: item.textColor }"
            alt=""
            aria-hidden="true"
          />
        </div>
        <div v-if="!item.icon" class="w-6 icon icon-default">
          <LocalizeWord :value="item.title"></LocalizeWord>
        </div>
        <div v-if="isTitleVisible" class="icon-title whitespace-nowrap">
          <LocalizeWord :value="item.title"></LocalizeWord>
        </div>
      </a>
    </template>
  </nav>
</template>

<style lang="postcss" scoped>
.navitem svg {
  @apply text-black-60% fill-current;
}

.navitem:focus-visible .icon {
  @apply outline-none shadow-outline;
}

.icon-title {
  @apply ml-3 md:hidden lg:block;
}

.icon-default {
  @apply rounded-full bg-primary-core font-light opacity-50 text-center h-6 flex flex-col justify-center;

  font-size: 0;
}

.icon-default ::first-letter {
  @apply text-xs text-white;
}

.sidebar > .nav-container:not(.expanded) a {
  @apply flex items-center;
}

@screen lg {
  .sidebar > .nav-container:not(.expanded) .icon-title {
    @apply opacity-0;

    transition: opacity 0.25s ease;
    transition-delay: 0.25s;
  }

  .sidebar > .nav-container.expanded .icon-title,
  .icon-title {
    @apply opacity-100;

    transition: opacity 0.25s ease;
  }
}
</style>
